import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, RoadworthinessTest, Vehicle } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['STATION_INSPECTOR', 'LTA_OFFICER'];
const CHECKLIST_ITEMS = ['Brakes', 'Lights & indicators', 'Tyres & tread depth', 'Steering & suspension', 'Emissions', 'Chassis & body', 'Seatbelts & horn'];

/** M21 — vehicle roadworthiness inspection station console: look up a vehicle, review history, submit a new test. */
export function Roadworthiness() {
  const { persona } = useAuth();
  const isInspector = persona ? persona.roles.some((r) => OFFICER_ROLES.includes(r)) : false;
  const [regNoInput, setRegNoInput] = useState('');
  const [regNo, setRegNo] = useState<string | null>(null);
  const [stationId, setStationId] = useState('STATION_MAHE_1');
  const [checklist, setChecklist] = useState<Record<string, boolean>>(() => Object.fromEntries(CHECKLIST_ITEMS.map((i) => [i, true])));
  const [result, setResult] = useState<'PASS' | 'FAIL'>('PASS');
  const [failReasons, setFailReasons] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const vehicleQ = useApiQuery<Vehicle>(regNo ? `/api/v1/transport/vehicles/${encodeURIComponent(regNo)}` : null);
  const historyQ = useApiQuery<Page<RoadworthinessTest>>(regNo ? `/api/v1/transport/vehicles/${encodeURIComponent(regNo)}/roadworthiness?limit=50` : null);

  const submit = async () => {
    if (!regNo) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/transport/roadworthiness', {
        regNo,
        stationId: stationId.trim(),
        checklist,
        result,
        failReasons: result === 'FAIL' ? failReasons.split(',').map((s) => s.trim()).filter(Boolean) : [],
      });
      historyQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record inspection');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title="Roadworthiness Inspections" subtitle="Station console: look up a vehicle, review its test history, and submit a new inspection (M21 — LTA)." />

      {!isInspector ? <RoleNotice roles={OFFICER_ROLES} /> : null}

      <div className="card mb-6 p-4">
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setRegNo(regNoInput.trim().toUpperCase());
          }}
        >
          <Field label="Vehicle registration no." className="flex-1">
            <input className="field-input" value={regNoInput} onChange={(e) => setRegNoInput(e.target.value)} placeholder="S 12345" />
          </Field>
          <button type="submit" className="btn-primary">
            Look up
          </button>
        </form>
      </div>

      {regNo ? (
        <>
          {vehicleQ.error ? <ErrorNote message={vehicleQ.error} /> : null}
          {vehicleQ.loading ? <Spinner /> : null}
          {vehicleQ.data ? (
            <div className="card mb-6 p-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-900">
                    {vehicleQ.data.regNo} — {vehicleQ.data.make} {vehicleQ.data.model} ({vehicleQ.data.year})
                  </div>
                  <div className="text-xs text-slate-500">
                    {vehicleQ.data.ownerType} · {vehicleQ.data.ownerRef}
                  </div>
                </div>
                <StatusBadge status={vehicleQ.data.status} />
              </div>

              {isInspector ? (
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="mb-2 text-sm font-semibold text-slate-700">Submit inspection</div>
                  {error ? (
                    <div className="mb-2">
                      <ErrorNote message={error} />
                    </div>
                  ) : null}
                  <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Field label="Station id">
                      <input className="field-input" value={stationId} onChange={(e) => setStationId(e.target.value)} />
                    </Field>
                    <Field label="Overall result">
                      <select className="field-input" value={result} onChange={(e) => setResult(e.target.value as 'PASS' | 'FAIL')}>
                        <option value="PASS">PASS</option>
                        <option value="FAIL">FAIL</option>
                      </select>
                    </Field>
                  </div>
                  <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {CHECKLIST_ITEMS.map((item) => (
                      <label key={item} className="flex items-center gap-2 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={checklist[item] ?? true}
                          onChange={(e) => setChecklist((c) => ({ ...c, [item]: e.target.checked }))}
                        />
                        {item}
                      </label>
                    ))}
                  </div>
                  {result === 'FAIL' ? (
                    <Field label="Fail reasons (comma-separated)" className="mb-3">
                      <input className="field-input" value={failReasons} onChange={(e) => setFailReasons(e.target.value)} placeholder="Worn brake pads, cracked windscreen" />
                    </Field>
                  ) : null}
                  <button className={result === 'PASS' ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={submit}>
                    {busy ? 'Recording…' : `Record ${result}`}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="card overflow-x-auto">
            <div className="border-b border-slate-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Test history</div>
            {historyQ.loading ? (
              <Spinner />
            ) : (historyQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No roadworthiness tests on file for this vehicle" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Inspected</th>
                    <th className="table-head">Station</th>
                    <th className="table-head">Result</th>
                    <th className="table-head">Cert ref</th>
                    <th className="table-head">Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {historyQ.data!.items.map((t) => (
                    <tr key={t.id}>
                      <td className="table-cell text-xs text-slate-500">{formatDateTime(t.inspectedAt)}</td>
                      <td className="table-cell text-xs">{t.stationId}</td>
                      <td className="table-cell">
                        <StatusBadge status={t.result} />
                      </td>
                      <td className="table-cell font-mono text-xs">{t.certRef ?? '—'}</td>
                      <td className="table-cell text-xs text-slate-500">{t.expiresAt ? formatDateTime(t.expiresAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
